import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMyEvents,
  getEventDetails,
  createEvent,
  updateEvent,
  deleteEvent,
  CreateEventData,
  Event,
  EventDetail,
} from "@/api/eventManagement";
import { toast } from "sonner";

export const useEvents = (userId: number, page: number = 1, limit: number = 20) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["events", userId, page, limit],
    queryFn: () => getMyEvents(userId, page, limit),
    enabled: !!userId,
  });

  const events = data?.events || [];

  const createMutation = useMutation({
    mutationFn: (eventData: CreateEventData) => createEvent(userId, eventData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", userId] });
      toast.success("Event created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create event");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: number; data: Partial<CreateEventData> }) =>
      updateEvent(userId, eventId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["events", userId] });
      queryClient.invalidateQueries({ queryKey: ["event", variables.eventId] });
      toast.success("Event updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update event");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (eventId: number) => deleteEvent(userId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", userId] });
      toast.success("Event deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete event");
    },
  });

  return {
    events,
    data, // Include full pagination data
    isLoading,
    error,
    createEvent: createMutation.mutateAsync,
    updateEvent: updateMutation.mutateAsync,
    deleteEvent: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export const useEventDetails = (userId: number, eventId: number) => {
  const queryClient = useQueryClient();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => getEventDetails(userId, eventId),
    enabled: !!userId && !!eventId,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["event", eventId] });
  };

  return {
    event,
    isLoading,
    error,
    refresh,
  };
};
